import HandlesServerResponses from "./HandlesServerResponses.mjs";
import {EXISTING_FORM_INPUT_VALUE} from "../config/vars.mjs";
import {isObject} from "@spoferan/spoferan-ts-core";

export default {

    mixins: [HandlesServerResponses],

    props: {
        // Whether to show an error alert on errors for GET-requests
        // For other requests an error alert is always shown for errors
        withError: {
            type: Boolean,
            default: false
        },

        // Whether to always send the request as a guest even though a user is authenticated to improve caching performance
        asGuest: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {

            // The controller to control the request currently being sent to the server
            pendingApiRequestController: null,

            apiRequestLoading: false,
            apiRequestsCount: 0

        }
    },

    methods: {

        /**
         * Make sure we have a relative path to our API for our proxy.
         *
         * @param action
         * @returns {string}
         */
        getApiEPUrl(action) {
            if (action.startsWith(this.$config.public.apiUrl)) {
                action = action.substr(this.$config.public.apiUrl.length);
            } else if (action.indexOf('/') < 0 && action.indexOf('.') >= 0) {
                // In here, we probably have a named api route
                action = this.$api(action)
            }

            return action;
        },

        /**
         * Recursively parses the specified data to be sent to the server.
         *
         * @param {object} data
         * @param {string|null} propNamePrefix
         */
        parseFormData(data, propNamePrefix = null) {
            let parsedData = {};

            if (data && Array.isArray(data) && data.length) {
                data.forEach((itValue, itKey) => {
                    const propName = propNamePrefix ? `${propNamePrefix}[${itKey}]` : itKey;
                    parsedData = {...parsedData, ...this.parseFormValueAsFlatObject(propName, itValue)}
                });
            } else if (data && isObject(data) && Object.keys(data).length) {
                Object.keys(data).forEach(itKey => {
                    const propName = propNamePrefix ? `${propNamePrefix}[${itKey}]` : itKey;
                    const itValue = data[itKey];
                    parsedData = {...parsedData, ...this.parseFormValueAsFlatObject(propName, itValue)}
                });
            } else if (propNamePrefix) {
                // We need to set an empty array for empty objects and arrays
                parsedData[propNamePrefix] = [];
            }

            return parsedData;
        },

        parseFormValueAsFlatObject(key, value) {
            let data = {};
            if (value instanceof FileList) {
                value = Array.from(value)
                for (let i = 0; i < value.length; i++) {
                    data[`${key}[${i}]`] = value[i] !== null && value[i] !== undefined ? value[i] : ''
                }
            } else if (Array.isArray(value) || (isObject(value) && !(value instanceof File))) {
                data = this.parseFormData(value, key)
            } else if (value !== EXISTING_FORM_INPUT_VALUE) {
                // We only add the value under the specified key when the value is not set to '_existing' which means that the existing value for the key shall be used on the server.
                // This enables us to simplify the logic on FormInputs, especially on FormFileInput component on which it's difficult to display the current value.
                data[key] = value !== null && value !== undefined ? value : ''
            }

            return data;
        },

        makeApiRequest(action, data = {}, method = 'get') {
            if (!action) {
                return;
            }

            action = this.getApiEPUrl(action);
            method = method.toUpperCase();

            // Stop the previous request if it is still pending
            if (this.pendingApiRequestController) {
                this.pendingApiRequestController.abort();
            }

            this.apiRequestLoading = true;
            this.pendingApiRequestController = new AbortController();

            return new Promise((resolve, reject) => {
                this.$apiFetch(action, {
                    method: method,
                    body: data,
                    guest: this.asGuest,
                    silent: true, // We handle the errors within the component itself
                    signal: this.pendingApiRequestController.signal
                }).then(response => {
                    this.apiRequestsCount++;
                    this.handleSuccess(response);
                    resolve(response);
                }).catch(error => {

                    // Check if error occurred due to missing internet connection of the client
                    if (this.$nuxt.isOffline) {
                        reject(this.$t('error.offline.message'), error);
                        return;
                    }

                    // Check if request was canceled
                    if (error.name === 'FetchError' && error.message.startsWith('The user aborted a request.')) {
                        return;
                    }

                    // We show the error only for non-get requests
                    if ((method !== 'GET' || this.withError)) {
                        this.handleError(error);
                    }

                    reject({
                        message: this.parseErrorMessage(error),
                        response: error
                    });
                }).finally(() => {
                    this.pendingApiRequestController = null;
                    this.apiRequestLoading = false;
                })
            })
        }
    }
}

